import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { TourLauncherButton } from "@/contexts/TourContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, DollarSign, Calendar, CheckCircle2, XCircle, PlayCircle, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * R154 — Special assessments (one-time levies).
 * Flow: draft → approve → bill → (open charges hit residents' ledgers).
 */
export default function SpecialAssessmentsPage() {
  const { properties, propertyId } = useProperty();
  const [scope, setScope] = useState(propertyId || properties[0]?.id || "");
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    reason: "",
    total_amount_usd: 100000,
    allocation_method: "flat_per_unit",
    installments: 12,
    first_due_date: new Date(Date.now() + 30 * 86400e3).toISOString().slice(0, 10),
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (properties.length && !scope) setScope(properties[0].id);
  }, [properties, scope]);

  const load = useCallback(async () => {
    if (!scope) return;
    try {
      const { data } = await api.get("/admin/special-assessments", { params: { property_id: scope } });
      setItems(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load"); }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/special-assessments",
        { property_id: scope, ...form,
          total_amount_usd: Number(form.total_amount_usd),
          installments: Number(form.installments) });
      toast.success(`Created — ${(data.assessment.allocation_method || "").replace(/_/g, " ")} allocation, residual ${data.assessment.residual_usd?.toFixed?.(2) || 0}`);
      setDialogOpen(false);
      setForm({ ...form, title: "", description: "", reason: "" });
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Create failed"); }
    finally { setBusy(false); }
  };

  const doAction = async (aid, action) => {
    if (action === "bill" && !window.confirm("Generate charges for all units + installments? This is idempotent — reruns are safe.")) return;
    if (action === "cancel" && !window.confirm("Cancel this assessment? Open charges will be voided.")) return;
    try {
      const { data } = await api.post(`/admin/special-assessments/${aid}/${action}`);
      if (action === "bill") toast.success(`Billed — ${data.charges_created || 0} charge rows created`);
      else toast.success(`${action}d`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || `${action} failed`); }
  };

  const scopeName = useMemo(
    () => properties.find((p) => p.id === scope)?.name || scope,
    [properties, scope],
  );

  return (
    <div className="space-y-4" data-testid="special-assessments-page">
      <PageHeader
        eyebrow="Finance"
        title="Special Assessments"
        subtitle="One-time levies (roof, hurricane, reserves). Approve, allocate across units, bill in installments."
        actions={<TourLauncherButton name="special_assessments_tour" />}
      />

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-64 h-9 bg-white/[0.04] border-white/10"
                          data-testid="sa-scope-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => setDialogOpen(true)}
                className="ml-auto bg-orange-500 hover:bg-orange-600 rounded-full h-9"
                data-testid="sa-new-btn" data-tour="sa-new">
          <Plus className="h-4 w-4 mr-1" /> New assessment
        </Button>
      </div>

      <Card className="p-0 border-white/10 overflow-hidden" data-tour="sa-history">
        <div className="p-4 border-b border-white/10 flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-orange-400" />
          <div className="overline">Assessments — {scopeName}</div>
          <Badge variant="outline" className="text-[10px] border-white/10">
            {items.length}
          </Badge>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-white/40 text-sm">
            No special assessments yet. Board approves a big project → click <strong>New assessment</strong>.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-xs">
              <tr>
                <th className="text-left px-3 py-2 text-white/60">Title</th>
                <th className="text-right px-3 py-2 text-white/60">Total</th>
                <th className="text-left px-3 py-2 text-white/60">Allocation</th>
                <th className="text-left px-3 py-2 text-white/60">Installments</th>
                <th className="text-left px-3 py-2 text-white/60">First due</th>
                <th className="text-left px-3 py-2 text-white/60">Status</th>
                <th className="text-right px-3 py-2 text-white/60"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-white/5"
                    data-testid={`sa-row-${a.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{a.title}</div>
                    {a.reason && <div className="text-[10px] text-white/50">{a.reason}</div>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    ${(a.total_amount_usd || 0).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-xs">{(a.allocation_method || "").replace(/_/g, " ")}</td>
                  <td className="px-3 py-2 text-xs">{a.installments || 1}×</td>
                  <td className="px-3 py-2 text-xs">{a.first_due_date}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={`text-[10px] ${
                      a.status === "billed" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" :
                      a.status === "approved" ? "border-orange-500/40 text-orange-300 bg-orange-500/10" :
                      a.status === "cancelled" ? "border-rose-500/40 text-rose-300 bg-rose-500/10" :
                      "border-white/20 text-white/60"
                    }`}>{a.status}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {a.status === "draft" && (
                      <button onClick={() => doAction(a.id, "approve")}
                              className="text-xs px-2 py-1 rounded-md border border-orange-500/40 text-orange-300 hover:bg-orange-500/10 mr-1"
                              data-testid={`sa-approve-${a.id}`}>
                        <CheckCircle2 className="h-3 w-3 inline mr-1" />Approve
                      </button>
                    )}
                    {(a.status === "approved" || a.status === "billed") && (
                      <button onClick={() => doAction(a.id, "bill")}
                              className="text-xs px-2 py-1 rounded-md border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 mr-1"
                              data-testid={`sa-bill-${a.id}`}>
                        <PlayCircle className="h-3 w-3 inline mr-1" />Bill{a.status === "billed" ? " again" : ""}
                      </button>
                    )}
                    {a.status !== "cancelled" && (
                      <button onClick={() => doAction(a.id, "cancel")}
                              className="text-xs px-2 py-1 rounded-md border border-white/10 text-white/50 hover:text-rose-300 hover:border-rose-500/40"
                              data-testid={`sa-cancel-${a.id}`}>
                        <XCircle className="h-3 w-3 inline mr-1" />Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New special assessment</DialogTitle>
            <DialogDescription className="text-xs text-white/60">
              Draft first, approve, then bill to generate resident charges.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title *</Label>
              <Input value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder="Roof replacement 2026"
                      className="bg-white/[0.04] border-white/10 mt-1"
                      data-testid="sa-title-input" />
            </div>
            <div>
              <Label className="text-xs">Reason / description</Label>
              <Textarea value={form.description} rows={2}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 text-sm" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Total ($)</Label>
                <Input type="number" min={0} step={100}
                        value={form.total_amount_usd}
                        onChange={(e) => setForm({ ...form, total_amount_usd: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 tabular-nums"
                        data-testid="sa-total-input" />
              </div>
              <div>
                <Label className="text-xs">Installments</Label>
                <Input type="number" min={1} max={60} step={1}
                        value={form.installments}
                        onChange={(e) => setForm({ ...form, installments: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1"
                        data-testid="sa-installments-input" />
              </div>
              <div>
                <Label className="text-xs">First due</Label>
                <Input type="date" value={form.first_due_date}
                        onChange={(e) => setForm({ ...form, first_due_date: e.target.value })}
                        className="bg-white/[0.04] border-white/10 mt-1 [color-scheme:dark]" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Allocation method</Label>
              <Select value={form.allocation_method}
                      onValueChange={(v) => setForm({ ...form, allocation_method: v })}>
                <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10 h-9"
                                data-testid="sa-allocation-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                  <SelectItem value="flat_per_unit">Flat per unit (equal split)</SelectItem>
                  <SelectItem value="pct_ownership">By ownership percentage</SelectItem>
                  <SelectItem value="sqft">By square footage</SelectItem>
                  <SelectItem value="unit_type">By unit type (set amounts)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">Cancel</Button>
            <Button onClick={create} disabled={busy}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="sa-create-btn">
              Create draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
